<template>
    <div
        class="vrm-modal vrm-modal--alert vrm-modal--alert--error vrm-modal--alert--wide mfp-with-anim"
    >
        <form class="vrm-modal__content" @submit.prevent="onSubmit" @reset.prevent="onReset">
            <div class="vrm-modal__title">
                <div class="vrm-modal__title-icon sg-vrm-icon_012_alarm-32px" />

                {{
                    errors.length +
                        ' ' +
                        (errors.length === 1
                            ? $t('firmware-library.issueFound', {
                                  i18nFallback: 'issue found',
                              })
                            : $t('firmware-library.issuesFound', {
                                  i18nFallback: 'issues found',
                              }))
                }}
            </div>
            <div class="vrm-modal__body">
                <vrm-scroll-fade>
                    <vrm-foldable-table
                        :items="errors"
                        :expanded="errors.length > 1 ? undefined : [errors[0].type]"
                        key-by="type"
                    >
                        <template v-slot:main="{ item: error, toggle, expanded }">
                            <vrm-table-cell-text
                                :text="errorTitles[error.type]"
                            ></vrm-table-cell-text>
                            <td v-if="errors.length > 1" class="vrm-table__actions">
                                <a
                                    class="vrm-table__icon--link"
                                    :class="
                                        expanded
                                            ? 'sg-vrm-icon_059_min-round-16px'
                                            : 'sg-vrm-icon_058_plus-round-16px'
                                    "
                                    @click="toggle"
                                />
                            </td>
                        </template>
                        <template v-slot:extra="{ item: error }">
                            <td colspan="4">
                                <div
                                    v-html="
                                        error.type === 'BadRequestException'
                                            ? error.message
                                            : errorDescriptions[error.type]
                                    "
                                ></div>
                                <br />
                                <template v-if="error.duplicateProductIds">
                                    <div
                                        v-for="[productId, files] of Object.entries(
                                            error.duplicateProductIds,
                                        )"
                                        :key="productId + 'duplicates'"
                                    >
                                        <div>
                                            {{
                                                $t('firmware-library.productId', {
                                                    i18nFallback: 'Product ID',
                                                }) + ': '
                                            }}
                                            <b>{{ productId }}</b>
                                        </div>
                                        <br />
                                        <div
                                            v-for="file in files"
                                            :key="file + Math.random()"
                                            class="vrm-form__file"
                                        >
                                            <div>
                                                <span class="sg-vrm-icon_044_file-16px" />
                                                {{ file }}
                                            </div>
                                            <i
                                                class="sg-vrm-icon_055_cross-16px"
                                                @click="onRemoveFile(file)"
                                            />
                                        </div>
                                        <br
                                            v-if="
                                                productId !==
                                                    Object.keys(error.duplicateProductIds).at(-1)
                                            "
                                        />
                                    </div>
                                </template>
                                <template v-else>
                                    <div
                                        v-for="file in error.firmwareFiles"
                                        :key="file + error.type"
                                        class="vrm-form__file"
                                    >
                                        <div>
                                            <span class="sg-vrm-icon_044_file-16px" />
                                            {{ file }}
                                        </div>
                                        <i
                                            class="sg-vrm-icon_055_cross-16px"
                                            @click="onRemoveFile(file)"
                                        />
                                    </div>
                                </template>
                            </td>
                        </template>
                    </vrm-foldable-table>
                </vrm-scroll-fade>
            </div>
            <div class="vrm-modal__main-button">
                <button type="submit" :disabled="!canContinue" class="sg-element sg-btn sg-blue">
                    {{
                        $t('common.continue', {
                            i18nFallback: 'Continue',
                        })
                    }}
                </button>
            </div>
        </form>
        <modal-close-handle />
    </div>
</template>
<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmTableCellFile from '@/vue/components/table/VrmTableCellFile.vue';
import VrmTableCellText from '@/vue/components/table/VrmTableCellText.vue';
import VrmScrollFade from '@/vue/components/shared/VrmScrollFade.vue';
import VrmFoldableTable from '@/vue/components/molecules/VrmFoldableTable.vue';
import i18n from '@/vue/languages';
@Component({
    components: {
        VrmFoldableTable,
        VrmScrollFade,
        VrmTableCellText,
        VrmTableCellFile,
        ModalCloseHandle,
    },
})
@AngularLocaleBundle('common')
export default class FirmwareLibraryFileIssuesModal extends Vue {
    @Prop({ type: Array, default: () => [] })
    errors;
    @Prop({ type: Array, required: true })
    files;
    @Prop({ type: Function, required: true })
    removeFile;
    @Prop({ type: Function, required: true })
    onContinue;

    async onSubmit() {
        await this.onContinue();
        this.$emit('close', true);
    }

    onReset() {
        this.$emit('close', false);
    }

    onRemoveFile(file) {
        for (let error of this.errors) {
            if (error.duplicateProductIds) {
                for (let productId in error.duplicateProductIds) {
                    error.duplicateProductIds[productId] = error.duplicateProductIds[
                        productId
                    ].filter(fileName => fileName !== file);
                }
            } else {
                error.firmwareFiles = error.firmwareFiles.filter(fileName => fileName !== file);
            }
        }
        this.removeFile(file);
    }

    get canContinue() {
        for (let error of this.errors) {
            if (error.duplicateProductIds) {
                for (let productId in error.duplicateProductIds) {
                    if (error.duplicateProductIds[productId].length > 1) return false;
                }
            } else {
                if (error.firmwareFiles.length > 0) return false;
            }
        }
        return true;
    }

    get errorTitles() {
        return {
            DuplicateFirmware: String(
                i18n.t('firmware-library.errors.duplicatesTitle', {
                    i18nFallback: 'Duplicated firmware file found',
                }),
            ),
            InvalidFileException: String(
                i18n.t('firmware-library.errors.invalidFormatTitle', {
                    i18nFallback: 'Invalid file format',
                }),
            ),
            BadRequestException: String(
                i18n.t('firmware-library.errors.badRequestTitle', {
                    i18nFallback: 'Bad request',
                }),
            ),
        };
    }

    get errorDescriptions() {
        return {
            DuplicateFirmware: String(
                i18n.t('firmware-library.errors.duplicatesDescription', {
                    i18nFallback:
                        'The uploaded files contain a duplicate firmware with the same product ID. Please only keep one file for each product ID before continuing',
                }),
            ),
            InvalidFileException: String(
                i18n.t('firmware-library.errors.invalidFormatDescription', {
                    i18nFallback:
                        'The uploaded file is a BETA firmware version. Please delete it to proceed.',
                }),
            ),
        };
    }
}
</script>
